import { AccessControlProvider } from "@refinedev/core";
import { newEnforcer, StringAdapter } from "casbin";
import { model } from "@/helpers/accessControl";
import { selectUserCan } from "@/helpers/utils";
import { AccessScope } from "@/types";

type Accesses = Record<string, AccessScope[]>;

const actionsLookup: Record<string, string> = {
  list: "read",
  show: "read",
  edit: "update",
  delete: "delete",
  create: "create",
  document_index: "document_index",
  premade_sync: "premade_sync",
  clear: "clear",
  read: "read",
};

export const createHasRole = (accesses: Accesses) => {
  return (subject: string, action: string, scope?: AccessScope): string => {
    const targetAction = actionsLookup[action] ?? "none";
    const enabled = selectUserCan(accesses, {
      subject,
      action: targetAction,
      scope,
    });
    return enabled ? action : "none";
  };
};

export const createHasRoles = (accesses: Accesses) => {
  const hasRole = createHasRole(accesses);
  return (
    subject: string,
    roles: [action: string, scope?: AccessScope][]
  ): string => {
    const result = roles
      .map(([action, scope]) => hasRole(subject, action, scope))
      .join(")|(");
    return "(" + result + ")";
  };
};

export const createAccessControlProvider = (
  accesses: Accesses,
  hasAccesses: boolean
): AccessControlProvider => {
  if (!hasAccesses) {
    return {
      can: async () => new Promise((resolve) => resolve({ can: false })),
      options: { buttons: { hideIfUnauthorized: true } },
    };
  }

  const hasRole = createHasRole(accesses);
  const hasRoles = createHasRoles(accesses);

  return {
    can: async ({ action, resource }) => {
      // The manage-subscriptions page is restricted to users who can create a
      // subscription for an org: Libertify.Subscription.create with ORG or ALL
      // scope (selectUserCan treats ALL as satisfying an ORG check).
      const canManageSubscriptions =
        hasRole("Subscription", "create", "ORG") !== "none";
      const permissions = `
        p, admin, users, ${hasRoles("User", [
          ["list", "ALL"],
          ["edit", "ALL"],
        ])}
        p, admin, user_invite, ${hasRoles("UserInvite", [
          ["list", "ALL"],
          ["show", "ALL"],
          ["update", "ALL"],
          ["create", "ALL"],
          ["delete", "ALL"],
          ["list", "ORG"],
          ["show", "ORG"],
          ["update", "ORG"],
          ["create", "ORG"],
          ["delete", "ORG"],
        ])}
        p, admin, cache, ${hasRoles("Cache", [
          ["clear", "ALL"],
          ["read", "ALL"],
        ])}
        p, admin, assets, ${hasRoles("Media.GenerationStep", [
          ["list"],
          ["show"],
        ])}
        p, admin, media, (list)
        p, admin, media_home, (list)
        p, admin, playbooks, (list)
        p, admin, feature_flags, ${hasRoles("Configuration", [
          ["list", "ORG"],
          ["read", "ALL"],
          ["edit", "ORG"],
          ["create", "ORG"],
          ["show", "ORG"],
          ["delete", "ORG"],
        ])}
        p, admin, media_dashboard, ${hasRoles("Media.GenerationStep", [
          ["list"],
          ["show"],
        ])}
        p, admin, media_media, ${hasRoles("Media.Media", [
          ["list", "ORG"],
          ["create", "ORG"],
          ["show", "ORG"],
        ])}
        p, admin, media_templates, ${hasRoles("Media.Template", [
          ["show", "ORG"],
        ])}
        p, admin, media_asset_libraries, ${hasRoles("Media.AssetLibrary", [
          ["list", "ALL"],
          ["edit", "ALL"],
          ["show", "ORG"],
          ["create", "ORG"],
          ["delete", "ORG"],
        ])}
        p, admin, media_assets, ${hasRoles("Media.Asset", [
          ["list", "ORG"],
          ["edit", "ORG"],
          ["show", "ORG"],
        ])}
        p, admin, media_assets_review, ${hasRoles("Media.Asset", [
          ["list", "ORG"],
          ["edit", "ORG"],
        ])}
        p, admin, media_documents, ${hasRoles("Media.Document", [
          ["list", "ORG"],
          ["edit", "ORG"],
          ["create", "ORG"],
          ["show", "ORG"],
          ["delete", "ORG"],
          ["document_index", "ORG"],
        ])}
        p, admin, media_batches, ${hasRoles("Media.Batch", [
          ["list"],
          ["show"],
        ])}
        p, admin, media_generation_steps, ${hasRoles("Media.GenerationStep", [
          ["list"],
          ["show"],
          ["update"],
        ])}
        p, admin, manage_subscriptions, ${
          canManageSubscriptions ? "(list)|(show)|(create)" : "(none)"
        }
        p, admin, subscriptions, ${hasRoles("Subscription", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, subscriptions_users, ${hasRoles(
          "Subscription.UserLicenseAssignment",
          [["list"], ["show"], ["create"], ["edit"], ["delete"]]
        )}
        p, admin, media_projects, ${hasRoles("Media.Project", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_analytics, ${hasRoles("Media.Project", [["list"]])}
        p, admin, media_brandkits, ${hasRoles("Media.BrandKit", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_voices, ${hasRoles("Media.VoiceProfile", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
          ["premade_sync"],
        ])}
        p, admin, media_faces, ${hasRoles("Media.FaceProfile", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_presenters, ${hasRoles("Media.PresenterProfile", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_ctas, ${hasRoles("Media.VoiceProfile", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_contacts, ${hasRoles("Media.Contact", [
          ["list"],
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
        ])}
        p, admin, media_products, ${hasRoles("Media.Product", [
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
          ["list"],
        ])}
        p, admin, media_pronunciations, ${hasRoles("Media.Pronunciation", [
          ["show"],
          ["create"],
          ["edit"],
          ["delete"],
          ["list"],
        ])}
        p, admin, organization, ${hasRoles("Organization", [
          ["list", "ORG"],
          ["show", "ORG"],
          ["create", "ORG"],
          ["edit", "ORG"],
          ["delete", "ORG"],
        ])}
        p, admin, media_organization_preferences, ${hasRoles(
          "Media.OrganizationPreference",
          [
            ["list", "ORG"],
            ["show", "ORG"],
            ["create", "ORG"],
            ["edit", "ORG"],
            ["delete", "ORG"],
          ]
        )}
        p, admin, onboarding_answer, (${hasRole(
          "Media.OnboardingAnswer",
          "create"
        )})
        p, admin, workspaces, ${hasRoles("Workspace", [
          ["list", "ORG"],
          ["show", "ORG"],
          ["create", "ORG"],
          ["edit", "ORG"],
          ["delete", "ORG"],
        ])}
        p, admin, media_theme_editor, ${hasRoles("Configuration", [
          ["list", "ALL"],
          ["read", "ALL"],
          ["show", "ALL"],
          ["create", "ORG"],
          ["edit", "ORG"],
          ["delete", "ORG"],
        ])}
      `;

      const adapter = new StringAdapter(permissions);
      const enforcer = await newEnforcer(model, adapter);

      return {
        can: await enforcer.enforce("admin", resource, action),
      };
    },
  };
};
